import type {
    ResolverAttributeEntry,
    ResolverCodeType,
    ResolverInputDto,
} from "../../../../../types/resolver";
import type { KeyValueEntry } from "./types";

/** Code entry shape for input draft (matches panel CodeBuilderEntry). */
export interface InputDraftCodeEntry {
    id: string;
    codeType: ResolverCodeType;
    code: string;
    original: boolean;
    units: string;
    attributes: ResolverAttributeEntry[];
    derivedAttributes: KeyValueEntry[];
}

/** Result of converting ResolverInputDto to draft; panel applies these and leaves configName unchanged. */
export interface ResolverInputDraftLoad {
    codes: InputDraftCodeEntry[];
    caseAttributes: ResolverAttributeEntry[];
    caseDerivedAttributes: KeyValueEntry[];
    workItemDataEntries: KeyValueEntry[];
}

const makeShortId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

function recordToKeyValueEntries(record: Record<string, unknown> | undefined): KeyValueEntry[] {
    if (!record || typeof record !== "object" || Array.isArray(record)) return [];
    return Object.entries(record).map(([key, value]) => ({
        id: makeShortId(),
        key,
        value: value != null ? String(value) : "",
    }));
}

function attributeDtoToEntry(
    dto: {
        questionId?: number;
        questionLabel?: string;
        answerSetId?: unknown;
        answerValueId?: unknown;
        answerDisplayText?: unknown;
    },
    index: number
): ResolverAttributeEntry {
    return {
        id: `attr-${index}-${makeShortId()}`,
        questionId: typeof dto.questionId === "number" ? dto.questionId : 0,
        questionLabel: typeof dto.questionLabel === "string" ? dto.questionLabel : "",
        answerSetId: dto.answerSetId != null ? dto.answerSetId : null,
        answerValueId: dto.answerValueId != null ? dto.answerValueId : null,
        answerDisplayText: dto.answerDisplayText != null ? dto.answerDisplayText : null,
    };
}

/** Convert resolver input DTO (e.g. from pasted JSON) to draft shape for panel state. */
export function resolverInputDtoToDraft(input: ResolverInputDto): ResolverInputDraftLoad {
    const codes: InputDraftCodeEntry[] = (input.codes ?? []).map((c, index) => ({
        id: `code-${index}-${makeShortId()}`,
        codeType: c.codeType ?? "DIAGNOSIS",
        code: typeof c.code === "string" ? c.code : "",
        original: Boolean(c.original),
        units: c.units != null ? String(c.units) : "",
        attributes: (c.attributes ?? []).map((a, i) => attributeDtoToEntry(a, i)),
        derivedAttributes: recordToKeyValueEntries(c.derivedAttributes as Record<string, unknown> | undefined),
    }));

    const caseAttributes: ResolverAttributeEntry[] = (input.caseAttributes ?? []).map((a, i) =>
        attributeDtoToEntry(a, i)
    );

    const caseDerivedAttributes = recordToKeyValueEntries(
        input.derivedAttributes as Record<string, unknown> | undefined
    );

    const workItemData = input.workItemData ?? {};
    const workItemDataEntries: KeyValueEntry[] =
        typeof workItemData === "object" && !Array.isArray(workItemData)
            ? Object.entries(workItemData).map(([key, value]) => ({
                  id: makeShortId(),
                  key,
                  value: value != null ? String(value) : "",
              }))
            : [];

    return {
        codes,
        caseAttributes,
        caseDerivedAttributes,
        workItemDataEntries,
    };
}

function resolveCodeType(rawType: string): ResolverCodeType | null {
    const normalized = rawType.trim().toUpperCase();
    if (normalized === "PROCEDURE" || normalized === "PROC") return "PROCEDURE";
    if (normalized === "DIAGNOSIS" || normalized === "DX") return "DIAGNOSIS";
    if (normalized === "MODIFIER" || normalized === "MOD") return "MODIFIER";
    return null;
}

/** Parse plaintext lines (e.g. pasted list of codes) into ResolverInputDto. */
export function plaintextToResolverInputDto(plaintext: string): ResolverInputDto {
    const lines = plaintext
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

    const codes = lines.map((line, index) => {
        const maybeTypeSeparator = line.indexOf(":");
        const maybeType = maybeTypeSeparator > 0 ? line.slice(0, maybeTypeSeparator) : "";
        const parsedCodeType = resolveCodeType(maybeType);
        const code = parsedCodeType ? line.slice(maybeTypeSeparator + 1).trim() : line;

        return {
            codeType: parsedCodeType ?? "DIAGNOSIS",
            id: index + 1,
            code,
            original: true,
            sequenceNumber: index + 1,
            attributes: [],
        };
    });

    return {
        codes,
        caseAttributes: [],
        workItemData: {},
        looseSpans: [],
    };
}
